import { getMockRoomForContractCall } from 'e2e-tests/mocks/mock-rooms';
import { isStreamPath } from 'e2e-tests/mocks/wagmi.mocks';
import { PublicClient } from 'viem';

import { MovesEnum, RoundStagesEnum, RoundWinnerOutcomes } from 'shared/constants/shared-enums';
import contractAbi from 'shared/contract/contract.abi.json';
import { isMocked } from 'shared/utils/isMocked';

interface ReadParams {
  provider: PublicClient;
  roomId: bigint;
  roundCounter?: bigint;
}

interface RspUniqRoomData {
  roomId: bigint;
  playerA: `0x${string}`;
  playerB: `0x${string}`;
  wins: number;
  token: `0x${string}`;
  bet: bigint;
}

interface RspPlayerWinCounter {
  winsPlayerA: number;
  winsPlayerB: number;
}

export type RspRoomTuple = [
  RspUniqRoomData,
  bigint,
  number,
  RspPlayerWinCounter,
  number,
  bigint,
  number,
  number,
];

export type RspRoundTuple = [
  MovesEnum,
  MovesEnum,
  `0x${string}`,
  `0x${string}`,
  RoundWinnerOutcomes,
  number,
  RoundStagesEnum,
];

const ZERO_HASH = '0x0000000000000000000000000000000000000000000000000000000000000000' as const;

const mockRound = (): RspRoundTuple => [
  MovesEnum.None,
  MovesEnum.None,
  ZERO_HASH,
  ZERO_HASH,
  RoundWinnerOutcomes.None,
  /* stage */ 0,
  /* roundStage */ RoundStagesEnum.InitGame,
];

const shouldUseMock = () => isMocked && !isStreamPath();

export const readRoom = async ({ provider, roomId }: ReadParams): Promise<RspRoomTuple> => {
  if (!provider) throw new Error('No public client');

  if (shouldUseMock()) {
    return getMockRoomForContractCall() as unknown as RspRoomTuple;
  }

  try {
    return (await provider.readContract({
      address: import.meta.env.VITE_RSP_CONTRACT_ADDRESS,
      abi: contractAbi,
      functionName: 'rooms',
      args: [BigInt(roomId)],
      authorizationList: undefined,
    })) as RspRoomTuple;
  } catch (err) {
    console.error('Error reading room:', err);
    throw new Error('Failed to read room');
  }
};

export async function readAllRoomRounds({
  provider,
  roomId,
}: {
  provider: PublicClient;
  roomId: bigint;
}) {
  try {
    return await provider.readContract({
      address: import.meta.env.VITE_RSP_CONTRACT_ADDRESS,
      abi: contractAbi,
      functionName: 'getAllRoomRounds',
      args: [BigInt(roomId)],
      authorizationList: undefined,
    });
  } catch (err) {
    console.error('Error reading room:', err);
    throw new Error('Failed to read room');
  }
}

export const readRounds = async ({ provider, roomId, roundCounter }: ReadParams) => {
  if (!provider) throw new Error('No public client');
  if (shouldUseMock()) return mockRound();
  return (await provider.readContract({
    address: import.meta.env.VITE_RSP_CONTRACT_ADDRESS,
    abi: contractAbi,
    functionName: 'rounds',
    args: [BigInt(roomId), BigInt(roundCounter!)],
    authorizationList: undefined,
  })) as RspRoundTuple;
};
